import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { useGetCatalogQuery, useGetReleaseKitsQuery } from '../../../../../redux/api/fymAPI';
import { useUserTokenDecoded } from '../../../../../redux/slices/appInfo/hooks';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { useQueryParam } from '../../../../../utils/hooks/useQueryParam';
import { TypedMemo } from '../../../../common/types';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useIsDemoAccount } from '../../../../accountSelector/useIsDemoAccount';
import { useMyTeamRole } from '../../../../team/useMyTeamRole';
import { useWorkspaceOwner } from '../../../../team/useWorkspaceOwner';
import {
  smlNavigationGroupMessages,
  smlNavigationMessages,
} from '../../../../music24/navigation/smlNavigationMessages';
import { SMLContainer } from '../../../smlContainer/SMLContainer';
import { useCatalogSong } from '../useCatalogSong';
import { parseSongPageSection } from './catalogSongPath';
import { SongPage } from './SongPage';
import { SongPageSkeleton } from './SongPageSkeleton';
import { songPageMessages } from './songPageMessages';
import { Panel } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

const MEMBER_NOTE_KEY = 'm24SongPageMemberNote';

function readMemberNoteDismissed(licenseId: number | undefined): boolean {
  try {
    return window.localStorage.getItem(`${MEMBER_NOTE_KEY}:${licenseId ?? 0}`) === '1';
  } catch {
    return false;
  }
}

function storeMemberNoteDismissed(licenseId: number | undefined): void {
  try {
    window.localStorage.setItem(`${MEMBER_NOTE_KEY}:${licenseId ?? 0}`, '1');
  } catch {
    // A private window can refuse the storage: the note comes back on the next visit.
  }
}

interface CatalogSongScreenProps {
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The route body of the song page (design song-page b1, f1 to f3): it finds the row in the
 * catalog (the cached list fills the head at once), reads the song, and shows loading, error and
 * not found. A 404 of the song read is "not found" too (another member removed the row).
 */
const CatalogSongScreenComponent: React.FC<CatalogSongScreenProps> = ({ accountId, catalogItemUuid }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const tab = parseSongPageSection(useQueryParam('tab', undefined, ''));
  const viewParam = useQueryParam('view', undefined, '');
  const catalog = useGetCatalogQuery({ accountId });
  const item = useMemo(
    () => catalog.data?.items.find(({ uuid }) => uuid === catalogItemUuid),
    [catalog.data, catalogItemUuid]
  );
  const songRead = useCatalogSong({ accountId, itemUuid: item === undefined ? undefined : catalogItemUuid });
  const kits = useGetReleaseKitsQuery({ accountId });
  const kit = useMemo(
    () => kits.data?.kits.find((summary) => summary.catalogItemUuid === catalogItemUuid),
    [catalogItemUuid, kits.data]
  );
  const readOnly = useIsDemoAccount();
  const role = useMyTeamRole();
  const owner = useWorkspaceOwner();
  const token = useUserTokenDecoded();
  const licenseId = token?.licenseId;
  const [isNoteDismissed, setIsNoteDismissed] = useState(() => readMemberNoteDismissed(licenseId));
  const dismissNote = useCallback(() => {
    storeMemberNoteDismissed(licenseId);
    setIsNoteDismissed(true);
  }, [licenseId]);

  const openCatalog = useCallback(() => router.push(SMLRoutes.catalog), [router]);
  const breadcrumbs = useMemo(
    () => [
      { name: intl.formatMessage(smlNavigationGroupMessages.yoursName) },
      { name: intl.formatMessage(smlNavigationMessages.catalog), onClick: openCatalog },
      ...(item === undefined ? [] : [{ name: item.title }]),
    ],
    [intl, item, openCatalog]
  );
  const { refetch } = catalog;
  const retryCatalog = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const songNotFound = (songRead.error as { statusCode?: number } | undefined)?.statusCode === 404;
  let body: React.ReactNode;
  if (item !== undefined && !songNotFound) {
    body = (
      <SongPage
        accountId={accountId}
        item={item}
        song={songRead.song}
        songError={songRead.error}
        retrySong={songRead.retry}
        kit={kit}
        breadcrumbs={breadcrumbs}
        initialSection={tab}
        initialView={viewParam === 'text' ? 'text' : undefined}
        readOnly={readOnly}
        role={role}
        ownerEmail={owner?.email ?? null}
        workspaceName={owner?.workspaceName ?? null}
        showMemberNote={!isNoteDismissed}
        onDismissMemberNote={dismissNote}
      />
    );
  } else if (catalog.isLoading) {
    body = <SongPageSkeleton />;
  } else if (catalog.error !== undefined) {
    body = (
      <Panel>
        <ErrorView error={catalog.error} retry={retryCatalog} />
      </Panel>
    );
  } else {
    body = (
      <Panel data-testid="song-page-not-found">
        <div className={styles.center}>
          <FontAwesomeIcon icon={faMagnifyingGlass} size="2x" />
          <b>{intl.formatMessage(songPageMessages.notFoundTitle)}</b>
          <span className={styles.help}>{intl.formatMessage(songPageMessages.notFoundBody)}</span>
          <SMLButton color="primary" omitCap onClick={openCatalog}>
            {intl.formatMessage(songPageMessages.openCatalog)}
          </SMLButton>
        </div>
      </Panel>
    );
  }

  return <SMLContainer header={null}>{body}</SMLContainer>;
};

export const CatalogSongScreen = TypedMemo(CatalogSongScreenComponent);
